import type { SimulatorItem } from '@vedalib/editor/lib/elements'
import React, { useLayoutEffect, useState } from 'react'

import { useScrollBorders } from 'components/LayoutPage/LayoutScroll'
import Button, { IconButton } from 'components/uiKit/Button'
import Dropdown, { Tooltip } from 'components/uiKit/Dropdown'
import { KitSize } from 'components/uiKit/KitTypes'
import { t } from 'services/Translation'

import AreaDropdownImage from '../AreaDropdownImage'
import * as s from './AreaDropdown.module.scss'

const IMAGE_WIDTH = 220
const INDENTS = 12

interface ISimulatorAreaDropdownProps {
  items: SimulatorItem[]
  onSelectItem: (id: string) => void
  onAddNewItem: () => void
  itemId?: string
  currentItemId?: string
  areaHeight?: number
  isVisibleItems: boolean
  setIsVisibleItems: React.Dispatch<React.SetStateAction<boolean>>
}

const AreaDropdown: React.FC<ISimulatorAreaDropdownProps> = ({
  items,
  itemId,
  onSelectItem,
  onAddNewItem,
  currentItemId,
  areaHeight = 0,
  isVisibleItems,
  setIsVisibleItems,
}) => {
  const [imgEl, setImgEl] = useState<HTMLImageElement | null>(null)

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsVisibleItems((prev) => !prev)
  }

  const handleScreenItemClick = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation()
    onSelectItem(itemId)
    setIsVisibleItems(false)
  }

  const handleAddNewClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onAddNewItem()
    setIsVisibleItems(false)
  }

  useLayoutEffect(() => {
    if (isVisibleItems && imgEl) {
      imgEl.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [isVisibleItems, imgEl])

  const borders = useScrollBorders({ direction: 'horizontal', type: 'shadow' })

  const onWheel = (e: React.WheelEvent) => {
    if (borders.refValue.current) {
      const { clientWidth, scrollWidth, scrollLeft } = borders.refValue.current
      const maxScrollLeft = scrollWidth - clientWidth

      borders.refValue.current.scrollLeft = Math.min(
        Math.max(scrollLeft + e.deltaY, 0),
        maxScrollLeft,
      )
    }
  }

  const scrollByDirection = (direction: 'left' | 'right') => (e: React.MouseEvent) => {
    e.stopPropagation()
    const container = borders.refValue.current
    if (container) {
      const delta = (IMAGE_WIDTH + INDENTS) * 3 * (direction === 'left' ? -1 : 1)
      container.scrollBy({ left: delta, behavior: 'smooth' })
    }
  }

  const DropdownContent = (
    <div className={s.content} onClick={(e) => e.stopPropagation()}>
      <div className={s.title}>{t('elements.simulator.itemsDropdown.title')}</div>
      <div className={s.contentWrapper}>
        <IconButton
          disabled={!borders.borders?.start}
          icon='otherLeft'
          iconSize={KitSize.M}
          name='prevItems'
          onClick={scrollByDirection('left')}
          styleType='secondary'
          round
        />
        <div
          className={s.itemsList}
          onScroll={borders.onScroll}
          onWheel={onWheel}
          ref={borders.ref}
        >
          {items.map((item, i) => {
            const isSelected = itemId === item.value
            const current = item.value === currentItemId
            return (
              <div
                className={s.item}
                key={item.value}
                onClick={(e) => !current && handleScreenItemClick(e, item.value)}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ref={(ref) => setImgEl(isSelected || current ? (ref as any) : null)}
              >
                <AreaDropdownImage
                  badge={`${t('elements.simulator.item')} ${i + 1} ${current ? t('elements.simulator.currentItem') : ''}`}
                  disabled={current}
                  image={item.image}
                  label={item.label}
                  overlay={item.overlay}
                  selected={itemId === item.value}
                />
              </div>
            )
          })}
        </div>
        <IconButton
          disabled={!borders.borders?.end}
          icon='otherRight'
          iconSize={KitSize.M}
          name='prevItems'
          onClick={scrollByDirection('right')}
          styleType='secondary'
          round
        />
      </div>
      <div className={s.footer}>
        <Button name='openResources' onClick={handleAddNewClick}>
          {t('elements.simulator.addItem')}
        </Button>
      </div>
    </div>
  )
  return (
    <div key={currentItemId} onClick={(e) => e.stopPropagation()}>
      <Dropdown
        offset={[areaHeight, -4]}
        onVisibleChange={setIsVisibleItems}
        overlay={DropdownContent}
        placement='bottom-start'
        styleType='clear'
        visible={isVisibleItems}
        fluid
      >
        {() => (
          <Tooltip
            overlay={t('elements.simulator.tooltip.createConnection')}
            trigger={['hoverWithoutContent']}
          >
            <Button
              active={isVisibleItems}
              name='chooseItem'
              onClick={handleButtonClick}
              size={KitSize.S}
              styleType='secondary'
            >
              {t('elements.simulator.connectTo')}
            </Button>
          </Tooltip>
        )}
      </Dropdown>
    </div>
  )
}

export default AreaDropdown
